import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  SvgIcon,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { capitalize, debounce, isEmpty } from 'lodash';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FilePlus01, LinkExternal01, Pencil02, Save01, SearchLG } from 'untitledui-js-base';
import { useCommonApi } from '../../api';
import { onSuccessCustomApiResponse } from '../../api/utils';
import { useNotify } from '../../hooks';
import { useGetAutocompleteAddress } from '../../hooks/useGetAutocompleteAddress';
import { PATHS } from '../../paths';
import { SeznamAutocomplete } from '../../types/external/seznamAutocomplete';
import { findCountryId } from '../../utils/countryMapping';
import { extractBirthDateFromBirthNumber } from '../../utils/extractBirthDateFromBirthNumber';
import { handleEnterAsTab } from '../../utils/handleEnterAsTab';
import {
  BasicDocumentValueItem,
  BoxWithHeader,
  DocumentItem,
  DocumentItemToUpload,
  LineClamp,
  RouterLink,
  TransparentChip
} from '../common';
import { BasicDialog } from '../common/BasicDialog';
import { GenerateDocumentButton } from '../common/GenerateDocumentButton';
import { SignatureDialog } from '../common/SignatureDialog';
import { useAuth } from '../hooks';
import { useAvailableRentalCar } from '../hooks/useAvailableRentalCar';
import { useLoadAresData } from '../hooks/useLoadAresData';
import { useRemoveAccidentFile } from '../hooks/useRemoveAccidentFile';
import { useUploadFiles } from '../hooks/useUploadFiles';
import { RentalCarSelectionModal } from '../rental/RentalCarSelectionModal';
import { Role } from '../types';
import {
  ACCIDENT_FILES,
  Accident,
  AccidentExistingUserResponse,
  AccidentGeneral,
  AccidentInsuranceList,
  AccidentRequest,
  AccidentServiceRequest,
  AccidentSourcesList,
  AccidentUserType,
  AresResponse,
  CarModelsList,
  MediaValue,
  PayoutType,
  PhotoNoteType,
  RegistryIdName,
  RegistryNameLabel,
  RegistryType,
  TenantType
} from '../types/accident';
import { SameAsValue } from './AccidenCreatingStepCustomerInformations';
import { AccidentAssigneeServiceDialog } from './AccidentAssigneeServiceDialog';
import { AcceptanceContent, ReasonType } from './AccidentCreatingStepDecision';
import { AccidentPhotographyBox } from './AccidentCreatingStepPhoto';
import { AccidentItem, BoxHeaderChip } from './AccidentItem';
import { AccidentOrvScanFields } from './AccidentOrvScanFields';
import { AccidentsPhotos } from './AccidentsPhotos';
import { DocumentActions } from './DocumentActions';
import { ServiceOrderFormDialog } from './ServiceOrderFormDialog';
import { AddNewAccountDialog } from './components/AddNewAccountDialog';
import { BirthNumberFields } from './components/BirthNumberFields';
import { ParticipantFields } from './components/ParticipantFieldsts';
import { ACCIDENT_REPORTING_OPTIONS, ACCIDENT_SOURCE_TYPE } from './constants';
import { useAccident } from './hooks/useAccident';
import { useAccount } from './hooks/useAccount';
import { useParticipantToggleLogic } from './hooks/useParticipantToggleLogic';
import { useSameAsHiding } from './hooks/useSameAsHiding';
import { parseAccidentPhotoNoteToAccident } from './parser';
import { cleanAccidentData } from './utils/cleanAccidentData';
import {
  getContactPersonSameAs,
  getDriverSameAs,
  initializeSameAsValues,
  isOperatorSameAsUser
} from './utils/sameAsCheckers';
import { applyAresData, applyExistingData, isStkExpired } from './utils/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UserPrefix = 'user' | 'operator';

interface AccidentMerchantContentProps {
  accident: Accident;
  onModalOpen: (name: string, limit: number, title?: string) => void;
  formRef: React.RefObject<HTMLFormElement>;
  onSubmit: (data: Accident) => void;
  onAresClick?: (vatNumber: string, isOperator?: boolean) => void;
  aresData?: AresResponse;
  isAresDataLoading?: boolean;
  acceptanceSources?: AccidentSourcesList[];
  onReasonChange?: (vote: AccidentGeneral) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AccidentMerchantContent = ({
  accident,
  onModalOpen,
  formRef,
  onSubmit,
  onAresClick: externalOnAresClick,
  aresData: externalAresData,
  isAresDataLoading: externalIsAresDataLoading,
  acceptanceSources,
  onReasonChange
}: AccidentMerchantContentProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { notifyOnSuccess, notifyOnError } = useNotify();

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [selectedDocumentName, setSelectedDocumentName] = useState<{
    documentName: ACCIDENT_FILES;
    tenant?: TenantType;
  } | null>(null);
  const [changeSourceModalOpen, setChangeSourceModalOpen] = useState(false);
  const [openServicesDialog, setOpenServicesDialog] = useState(false);
  const [commissionType, setCommissionType] = useState<string>('Kč');
  const [onPlace, setOnPlace] = useState(1);
  const [reasonValue, setReasonValue] = useState<ReasonType>();
  const [insuranceValue, setInsuranceValue] = useState<string>();
  const [accidentDate, setAccidentDate] = useState<Date | null>(new Date());

  // ─── Participant state ─────────────────────────────────────────────────────
  const [contactSameAsOwner, setContactSameAsOwner] = useState<SameAsValue>(() => getContactPersonSameAs(accident));
  const [driverSameAsOwner, setDriverSameAsOwner] = useState<SameAsValue>(() => getDriverSameAs(accident));
  const [operatorSameAsOwner, setOperatorSameAsOwner] = useState<boolean>(() => isOperatorSameAsUser(accident));
  const [userType, setUserType] = useState<AccidentUserType>(AccidentUserType.Person);
  const [operatorType, setOperatorType] = useState<AccidentUserType>(AccidentUserType.Person);
  const [existingUserData, setExistingUserData] = useState<AccidentExistingUserResponse | undefined>(undefined);
  const [existingOperatorData, setExistingOperatorData] = useState<AccidentExistingUserResponse | undefined>(undefined);

  // ─── Hooks ────────────────────────────────────────────────────────────────
  const { handleScanTP, openScanner, setOpenScanner, carData, isLoadingCarData, handleGetCarData, orv, setOrv } =
    useAccident();
  const { handleSetImage, isUploadingImages } = useUploadFiles(accident);
  const { handleRemoveFile, isRemoving } = useRemoveAccidentFile({
    accidentId: accident?.id as number
  });
  const {
    handleGetAvailableRentalCars,
    isSearchingRentalCars,
    availableCars,
    setAvailableCars,
    handleRentCar,
    isProcessingRent
  } = useAvailableRentalCar();
  const {
    handleGetAccountEmail,
    accountResult,
    setAddNewAccountDialogOpen,
    addNewAccountDialogOpen,
    isCreatingAccount,
    handleCreateAccount
  } = useAccount();

  // ARES - oddělený pro user a operator
  const {
    isAresDataLoading: isUserAresLoading,
    aresData: userAresData,
    handleLoadAresData: handleLoadUserAresData
  } = useLoadAresData();
  const {
    isAresDataLoading: isOperatorAresLoading,
    aresData: operatorAresData,
    handleLoadAresData: handleLoadOperatorAresData
  } = useLoadAresData();
  const isAresDataLoading = isUserAresLoading || isOperatorAresLoading || externalIsAresDataLoading;

  // Autocomplete adresa - oddělený pro user a operator
  const { handleOnSearch, autocompleteItems, isLoadingData } = useGetAutocompleteAddress();
  const {
    handleOnSearch: handleOnSearchOperator,
    autocompleteItems: operatorAutocompleteItems,
    isLoadingData: isLoadingOperatorData
  } = useGetAutocompleteAddress();

  // ─── Queries ──────────────────────────────────────────────────────────────
  const { data: carBrandsAndModels } = useQuery<CarModelsList[]>(
    useCommonApi().getRegistryList(RegistryType.CAR_MODELS)
  );
  const { data: insuranceTypes } = useQuery<RegistryNameLabel[]>(
    useCommonApi().getRegistryList(RegistryType.INSURANCE_TYPES)
  );
  const { data: insurances } = useQuery<AccidentInsuranceList[]>(
    useCommonApi().getRegistryList(RegistryType.INSURANCES)
  );
  const { data: countryList } = useQuery<RegistryIdName[]>(useCommonApi().getRegistryList(RegistryType.COUNTRY_LIST));
  const { data: servicesTypes, isLoading: isLoadingServices } = useQuery<RegistryIdName[]>({
    ...useCommonApi().getRegistryList(RegistryType.SERVICES),
    enabled: openServicesDialog
  });
  const { data: activeAccidentService, isLoading: isLoadingActiveAccidentService } = useQuery(
    useCommonApi().getAccidentService(accident.id)
  );

  // ─── Mutations ────────────────────────────────────────────────────────────
  const { getAccident } = useCommonApi();
  const { mutate: updateAccidentData } = useMutation(useCommonApi().updateAccident());
  const { mutate: assigneeServiceToAccident, isPending: isAssigningServiceToAccident } = useMutation(
    useCommonApi().assigneeServiceToAccident()
  );
  const { mutate: signAccidentDocument, isPending: isGeneratingDocument } = useMutation(
    useCommonApi().signAccidentDocument()
  );
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const { mutate: getExistingUser } = useMutation(useCommonApi().getExistingUser());

  // ─── Form ─────────────────────────────────────────────────────────────────
  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    reset
  } = useForm<Accident>({
    shouldUnregister: false,
    defaultValues: accident
  });

  const hasLeasing = useWatch({ control, name: 'car.hasLeasing' });

  const ico = watch('user.company.companyIdNum');
  const operatorIco = watch('operator.company.companyIdNum');
  const birthNumber = watch('user.birthNumber');
  const operatorBirthNumber = watch('operator.birthNumber');
  const driverBirthNumber = watch('driver.birthNumber');
  const selectedBrand = watch('car.brand');
  const notificationEmail = watch('notification.email');
  const notificationPhone = watch('notification.phone');
  const stkValue = watch('car.stk');
  const watchedAccidentDate = watch('date');
  const stkExpired = isStkExpired(stkValue, watchedAccidentDate || accident?.date);

  const isCompany = userType === AccidentUserType.Company || userType === AccidentUserType.BusinessMan;
  const isOperatorCompany = operatorType === AccidentUserType.Company || operatorType === AccidentUserType.BusinessMan;
  const availableModels = Array.isArray(carBrandsAndModels)
    ? carBrandsAndModels.find((brand) => brand.title === selectedBrand)?.models || []
    : [];

  const {
    shouldHideUserOptionForDriver,
    shouldHideOperatorOptionForDriver,
    shouldHideUserOptionForContact,
    shouldHideOperatorOptionForContact,
    shouldHideDriverOptionForContact
  } = useParticipantToggleLogic({
    userType,
    operatorType,
    operatorSameAsOwner,
    driverSameAsOwner
  });

  // ─── Effects: inicializace dat z API ──────────────────────────────────────

  useEffect(() => {
    if (!accident) return;
    reset(accident);
    setOrv(accident.car?.licenseId ?? '');

    const sameAsValues = initializeSameAsValues(accident);
    setOperatorSameAsOwner(sameAsValues.operatorSameAsOwner);
    setDriverSameAsOwner(sameAsValues.driverSameAsOwner);
    setContactSameAsOwner(sameAsValues.contactSameAsOwner);

    if (accident?.user?.type?.name) setUserType(accident.user.type.name as AccidentUserType);
    if (accident?.operator?.type?.name && !sameAsValues.operatorSameAsOwner)
      setOperatorType(accident.operator.type.name as AccidentUserType);

    setValue('user.address.countryId', accident?.user?.address?.country?.id);
    setValue('operator.address.countryId', accident?.operator?.address?.country?.id); // FIX: operator country

    const rawValue = accident?.source?.commissionValue || '';
    const match = rawValue.match(/^(\d+(?:[.,]\d+)?)?\s*(%)?$/);
    if (match) {
      const [, value, percent] = match;
      setCommissionType(percent ? '%' : 'Kč');
      setValue('source.commissionValue', value ? value.replace(',', '.') : '');
    } else {
      setCommissionType('Kč');
      setValue('source.commissionValue', rawValue.replace(/[^0-9.,]/g, ''));
    }
  }, [accident]);

  // Country se načítá asynchronně - po načtení nastav u obou
  useEffect(() => {
    if (!countryList) return;
    setValue('user.address.countryId', accident?.user?.address?.country?.id);
    setValue('operator.address.countryId', accident?.operator?.address?.country?.id); // FIX: operator country
  }, [countryList]);

  // ─── Effects: ARES ────────────────────────────────────────────────────────

  useEffect(() => {
    applyAresData('user', userAresData, setValue, userType);
  }, [userAresData]);
  useEffect(() => {
    applyAresData('user', externalAresData, setValue, userType);
  }, [externalAresData]);
  useEffect(() => {
    applyAresData('operator', operatorAresData, setValue, operatorType);
  }, [operatorAresData]);

  // ─── Effects: existující data ze systému ──────────────────────────────────

  useEffect(() => {
    applyExistingData('user', existingUserData, isCompany, setValue);
  }, [existingUserData]);
  useEffect(() => {
    applyExistingData('operator', existingOperatorData, isOperatorCompany, setValue);
  }, [existingOperatorData]);

  // ─── Effects: synchronizace equalTo do RHF state ──────────────────────────

  useEffect(() => {
    setValue('operator.equalTo' as any, operatorSameAsOwner ? 'user' : 'none');
  }, [operatorSameAsOwner]);
  useEffect(() => {
    setValue('driver.equalTo' as any, driverSameAsOwner);
  }, [driverSameAsOwner]);
  useEffect(() => {
    setValue('contactPerson.equalTo' as any, contactSameAsOwner);
  }, [contactSameAsOwner]);

  // ─── Effects: odvození data narození z RČ ────────────────────────────────

  useEffect(() => {
    const pairs: Array<[string | undefined, 'user.birthDate' | 'operator.birthDate' | 'driver.birthDate']> = [
      [birthNumber, 'user.birthDate'],
      [operatorBirthNumber, 'operator.birthDate'],
      [driverBirthNumber, 'driver.birthDate']
    ];
    pairs.forEach(([bn, field]) => {
      const date = extractBirthDateFromBirthNumber(bn ?? '');
      if (date) setValue(field, date);
    });
  }, [birthNumber, operatorBirthNumber, driverBirthNumber]);

  // ─── Effects: auto-přepnutí při skrytí možností ───────────────────────────

  useSameAsHiding(
    driverSameAsOwner,
    setDriverSameAsOwner,
    shouldHideUserOptionForDriver,
    shouldHideOperatorOptionForDriver
  );

  useSameAsHiding(
    contactSameAsOwner,
    setContactSameAsOwner,
    shouldHideUserOptionForContact,
    shouldHideOperatorOptionForContact
  );

  // ─── Effects: leasing + OBD scan ─────────────────────────────────────────

  useEffect(() => {
    setValue('car.leasingNumber', hasLeasing ? accident?.car?.leasingNumber ?? '' : '');
  }, [hasLeasing, setValue]);

  useEffect(() => {
    if (carData && !isEmpty(carData.response)) {
      reset({
        car: {
          brand: capitalize(carData?.response?.TovarniZnacka),
          model: capitalize(carData?.response?.ObchodniOznaceni),
          manufacturedYear: carData.response.DatumPrvniRegistrace,
          stk: carData.response.PravidelnaTechnickaProhlidkaDo,
          vin: carData?.response.VIN,
          licenseId: orv
        }
      });
    }
  }, [carData]);

  useEffect(() => {
    setValue('car.licenseId', orv);
  }, [orv, setValue]);

  useEffect(() => {
    setValue('operator.type.name' as any, operatorSameAsOwner ? userType : operatorType);
  }, [operatorType, operatorSameAsOwner, userType]);

  useEffect(() => {
    setValue('user.type.name' as any, userType);
  }, [userType]);

  // ─── Handlers: účastníci ──────────────────────────────────────────────────

  const handleAresClick = (vatNumber: string, isOperator?: boolean) => {
    if (isOperator) {
      handleLoadOperatorAresData(vatNumber);
    } else {
      handleLoadUserAresData(vatNumber);
      externalOnAresClick?.(vatNumber, false);
    }
  };

  const handleAutocompleteSearch = (value: string, isOperator?: boolean) => {
    isOperator ? handleOnSearchOperator(value) : handleOnSearch(value);
  };

  const handleAutocompleteSelect = (value: SeznamAutocomplete, isOperator: boolean) => {
    const prefix: UserPrefix = isOperator ? 'operator' : 'user';
    const find = (type: string) => value.data.regionalStructure?.find((item) => item.type === type)?.name ?? '';

    const municipality = find('regional.municipality');
    const municipalityPart = find('regional.municipality_part');
    const cityParts = [municipality];
    if (municipalityPart && municipalityPart !== municipality) cityParts.push(municipalityPart);

    setValue(`${prefix}.address.street` as any, find('regional.street'));
    setValue(`${prefix}.address.houseNum` as any, find('regional.address'));
    setValue(`${prefix}.address.city` as any, cityParts.filter(Boolean).join(', '));
    setValue(`${prefix}.address.zip` as any, value.data.zip);
    const countryId = findCountryId(find('regional.country'), countryList);
    if (countryId !== undefined) setValue(`${prefix}.address.countryId` as any, countryId);
  };

  const handleGetExistingUser = (identification: string, isOperator?: boolean) => {
    const type = (isOperator ? isOperatorCompany : isCompany) ? 'company' : 'person';
    getExistingUser(
      { identification, type },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: (_, data) => {
            isOperator ? setExistingOperatorData(data) : setExistingUserData(data);
          }
        })
      }
    );
  };

  const handleOperatorSameAsOwnerChange = (checked: boolean) => {
    setOperatorSameAsOwner(checked);
    if (!checked) {
      setOperatorType(AccidentUserType.Person);
      setValue('operator.type.name' as any, AccidentUserType.Person);
    }
  };

  // ─── Handlers: form submit ────────────────────────────────────────────────

  const handleSubmitForm = (data: Accident) => {
    const cleanedData = cleanAccidentData(data);

    onSubmit({
      ...cleanedData,
      ...(cleanedData.source && {
        source: {
          ...cleanedData.source,
          commissionValue: `${cleanedData.source.commissionValue ?? ''}${commissionType}`
        }
      })
    });
  };

  // ─── Handlers: ostatní ────────────────────────────────────────────────────

  const handleSubmitReasonChange = () => {
    onReasonChange?.({
      isSpotSolved: Boolean(onPlace),
      date: format(accidentDate ?? new Date(), 'yyyy-MM-dd HH:mm'),
      source: { ...reasonValue }
    });
    setChangeSourceModalOpen(false);
  };

  const handleAssigneeServiceToAccident = ({
    data,
    isUpdate
  }: {
    data: AccidentServiceRequest;
    isUpdate?: boolean;
  }) => {
    assigneeServiceToAccident(
      {
        accidentId: accident.id,
        method: isUpdate ? 'patch' : undefined,
        ...data
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => setOpenServicesDialog(false)
        })
      }
    );
  };

  const debouncedUpdatePhotoNote = debounce((data: PhotoNoteType) => {
    updateAccidentData({
      id: accident.id.toString(),
      accidentData: { mediaGroups: { [data.photoName]: data.note } }
    });
  }, 500);

  const handleAccidentDateChange = (newDate: Date) => {
    updateAccidentData(
      {
        id: accident.id.toString(),
        accidentData: { accident: { date: format(newDate, 'yyyy-MM-dd HH:mm') } as AccidentRequest['accident'] }
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            queryClient.invalidateQueries({ queryKey: getAccident(accident.id.toString()).queryKey });
            queryClient.invalidateQueries({ queryKey: ['common', 'accidents'] });
            notifyOnSuccess('Datum nehody bylo upraveno!');
          },
          failed: () => notifyOnError('Chyba při úpravě data nehody!')
        })
      }
    );
  };

  const handleSignDocument = (signatureBase64: string) => {
    if (!selectedDocumentName) return null;
    signAccidentDocument(
      {
        document: selectedDocumentName.documentName,
        tenantType: selectedDocumentName.tenant,
        accidentId: accident?.id,
        files: [
          {
            filename: 'sign.png',
            data: signatureBase64,
            template: 'tenantSignature'
          }
        ]
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            notifyOnSuccess('Dokument byl podepsán!');
            setSelectedDocumentName(null);
          },
          failed: () => notifyOnError('Chyba při podepisování dokumentu!')
        })
      }
    );
  };

  const handleGenerateDocument = (documentName: string, regenerate?: boolean, tenantType?: TenantType) => {
    if (!documentName) return null;
    signAccidentDocument(
      {
        document: documentName,
        accidentId: accident?.id,
        forceRegenerate: regenerate,
        tenantType
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => notifyOnSuccess('Dokument byl vygenerovaný!'),
          failed: () => notifyOnError('Chyba při generování dokumentu!')
        })
      }
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <form onSubmit={handleSubmit(handleSubmitForm)} ref={formRef}>
        <Grid container spacing={2}>
          {/* ══════════════════════════════════════════════════
              SEKCE 1: Přehled nehody + vozidlo + doplňky
          ══════════════════════════════════════════════════ */}

          {/* Přehled nehody */}
          <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
            <AccidentItem
              {...accident}
              userRole={user?.role}
              onDateChange={handleAccidentDateChange}
              extra={
                user?.role === Role.Dealer && (
                  <>
                    <Box pt={0.5}>
                      <Divider />
                      <Stack pt={2} direction="row" justifyContent="space-between" width={1}>
                        <Typography fontWeight="bold" color="success.main">
                          Výše provize
                        </Typography>
                        <Typography fontWeight="bold" color="success.main">
                          {accident?.dealerCommission ? `${accident.dealerCommission} %` : '----'}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box my={1}>
                      <Divider />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight="bold">Autopůjčovna</Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                          handleGetAvailableRentalCars({
                            rentalDate: new Date()
                          })
                        }
                        disabled={!!accident?.carRental?.find((rental) => rental.isBlocked)}
                      >
                        Nová zápůjčka
                      </Button>
                    </Stack>
                    {accident.carRental && !isEmpty(accident.carRental) && (
                      <Stack gap={1}>
                        {accident.carRental.map((rental) => (
                          <Stack
                            key={rental.id}
                            gap={1}
                            sx={{
                              color: rental.isRunning ? 'success.main' : 'textDisabled.main',
                              fontSize: '12px',
                              borderBottom: '1px solid',
                              borderColor: 'grey.300',
                              paddingBottom: '8px'
                            }}
                          >
                            {rental?.isBlocked && (
                              <Stack gap={1} direction="row" alignItems="center">
                                <TransparentChip
                                  size="small"
                                  label="Blokovaná"
                                  color="warning"
                                  variant="filled"
                                  sx={{
                                    fontSize: '10px',
                                    height: '15px',
                                    maxWidth: 'fit-content'
                                  }}
                                />
                                {rental.reservationDate && (
                                  <TransparentChip
                                    size="small"
                                    label={`Do: ${format(rental.reservationDate, 'dd.MM.yyyy HH:mm')}`}
                                    color="default"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '10px',
                                      height: '15px',
                                      maxWidth: 'fit-content'
                                    }}
                                  />
                                )}
                              </Stack>
                            )}
                            <Stack direction="row" spacing={1} alignItems="center">
                              <SvgIcon color="inherit" fontSize="inherit">
                                <LinkExternal01 />
                              </SvgIcon>
                              <RouterLink to={PATHS.CAR_RENTAL(rental.id)}>
                                <Link
                                  component="span"
                                  fontSize="12px"
                                  sx={{
                                    color: 'inherit',
                                    textDecoration: rental.isReturned ? 'line-through' : 'underline'
                                  }}
                                  underline="none"
                                >
                                  {`${format(rental.rentalDate, 'dd.MM.yyyy')}${rental.returnDate && ` - ${format(rental.returnDate, 'dd.MM.yyyy')}`} - ${[rental.car.brand, rental.car.model].filter(Boolean).join(' ')}, ${rental.car?.plateNumber ?? ''}`}
                                </Link>
                              </RouterLink>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </>
                )
              }
            />
          </Grid>

          {/* Údaje o vozidle */}
          <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
            <BoxWithHeader
              title="Údaje o vozidle"
              fullHeight
              hasError={!accident.status?.progress?.groups?.car?.isCompleted}
              header={
                !accident.status?.progress?.groups?.car?.isCompleted && (
                  <BoxHeaderChip label="Vyžaduje doplnění" color="error" />
                )
              }
            >
              <Stack gap={2}>
                <AccidentOrvScanFields
                  technicalIdNumber={orv}
                  onTechnicalIdChange={setOrv}
                  closeScannerClick={() => setOpenScanner(false)}
                  openScanner={openScanner}
                  openScannerClick={() => setOpenScanner(true)}
                  onGetCarClick={handleGetCarData}
                  isLoadingCarData={isLoadingCarData}
                  onScan={handleScanTP}
                  compact
                />
                <Box mb={3} mt={1}>
                  <Divider />
                </Box>
                <Controller
                  control={control}
                  name="car.plateNumber"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      required
                      value={value}
                      onChange={onChange}
                      label="SPZ"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.car?.plateNumber)}
                      helperText={errors.car?.plateNumber?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="car.vin"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label="VIN"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.car?.vin)}
                      helperText={errors.car?.vin?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="car.brand"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      disablePortal
                      freeSolo
                      value={value}
                      options={Array.isArray(carBrandsAndModels) ? carBrandsAndModels.map((item) => item.title) : []}
                      onChange={(_, newValue) => {
                        onChange(newValue);
                        const models = Array.isArray(carBrandsAndModels)
                          ? carBrandsAndModels.find((b) => b.title === newValue)?.models || []
                          : [];
                        setValue('car.model', models?.[0]?.title || '', {
                          shouldValidate: true
                        });
                      }}
                      renderInput={(params) => <TextField {...params} required label="Značka vozu" fullWidth />}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="car.model"
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      disablePortal
                      freeSolo
                      value={value}
                      options={availableModels?.map((item) => item.title)}
                      onChange={(_, newValue) => onChange(newValue)}
                      renderInput={(params) => <TextField {...params} required label="Model vozu" fullWidth />}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="car.manufacturedYear"
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      label="Rok výroby *"
                      value={value ? parseISO(value) : null}
                      onChange={(date) => onChange(date ? format(date, 'yyyy') : '')}
                      format="yyyy"
                      views={['year']}
                      maxDate={new Date()}
                      slotProps={{
                        textField: {
                          error: Boolean(errors.car?.manufacturedYear),
                          helperText: errors.car?.manufacturedYear?.message
                        }
                      }}
                    />
                  )}
                />
                <Box>
                  <Controller
                    control={control}
                    name="car.stk"
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        label="Platnost STK"
                        value={value ? new Date(value) : null}
                        onChange={onChange}
                        sx={{ width: 1 }}
                        slotProps={{
                          textField: {
                            error: Boolean(errors.car?.stk),
                            helperText: errors.car?.stk?.message
                          }
                        }}
                      />
                    )}
                  />
                  {stkExpired && (
                    <Box mt={1}>
                      <TransparentChip label="STK je prošlá" color="error" variant="filled" />
                    </Box>
                  )}
                </Box>
                <Controller
                  control={control}
                  name="car.speedometerValue"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label="Stav Km"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.car?.speedometerValue)}
                      helperText={errors.car?.speedometerValue?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <FormControl fullWidth>
                  <InputLabel id="tankvalue-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Stav nádrže u přebírání auta
                  </InputLabel>
                  <Controller
                    control={control}
                    name="car.tankValue"
                    render={({ field: { onChange, value } }) => (
                      <Select value={value} onChange={onChange} labelId="tankvalue-label">
                        {['Neznámá', 'Prázdná', 'Čtvrt', 'Půl', 'Tři čtvrtě', 'Plná'].map((v) => (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="insurance-type-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Druh pojištění
                  </InputLabel>
                  <Controller
                    control={control}
                    name="insuranceType"
                    render={({ field }) => (
                      <Select {...field} labelId="insurance-type-label">
                        {insuranceTypes?.map((type) => (
                          <MenuItem key={type.name} value={type.name}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="insurance-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Pojišťovna
                  </InputLabel>
                  <Controller
                    control={control}
                    name="insurance.id"
                    render={({ field: { onChange, value } }) => (
                      <Select value={value} onChange={onChange} labelId="insurance-label">
                        {insurances?.map((ins) => (
                          <MenuItem key={ins.id} value={ins.id}>
                            {ins.title}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="insurance-report-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Hlášení na pojišťovnu
                  </InputLabel>
                  <Controller
                    control={control}
                    name="accidentReporting.type"
                    render={({ field }) => (
                      <Select {...field} labelId="insurance-report-label">
                        {ACCIDENT_REPORTING_OPTIONS?.map((type) => (
                          <MenuItem key={type.type} value={type.type}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Stack>
            </BoxWithHeader>
          </Grid>

          {/* Doplňkové informace */}
          <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
            <BoxWithHeader
              title={
                <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={1}>
                  <Typography variant="h6" fontWeight="bold">
                    Doplňkové informace
                  </Typography>
                  <Button variant="outlined" color="info" onClick={() => setChangeSourceModalOpen(true)}>
                    Upravit zdroj
                  </Button>
                </Stack>
              }
              fullHeight
              hasError={!accident.status?.progress?.groups?.car?.isCompleted}
            >
              <Stack gap={2}>
                {isLoadingActiveAccidentService ? (
                  <Stack justifyContent="center" width={1} direction="row" py={2}>
                    <CircularProgress color="primary" size="20px" />
                  </Stack>
                ) : activeAccidentService && !isEmpty(activeAccidentService) ? (
                  <Stack spacing={2}>
                    <Stack spacing={1} direction="row">
                      <TextField
                        label="Servis"
                        value={activeAccidentService?.sourceName}
                        aria-readonly
                        disabled
                        fullWidth
                      />
                      <IconButton color="primary" onClick={() => setOpenServicesDialog(true)}>
                        <Pencil02 size="20px" />
                      </IconButton>
                    </Stack>
                    <Stack>
                      <LineClamp maxLines={1} fontWeight="bold">
                        Zakázkový list
                      </LineClamp>
                      <Box my={1}>
                        <Divider />
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        {activeAccidentService.orderForm ? (
                          <>
                            <Chip variant="outlined" label="Podepsáno" color="success" />
                            <Tooltip title="Podepsat dokument">
                              <IconButton color="primary" size="small" onClick={() => setOrderFormOpen(true)}>
                                <Pencil02 size="20px" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <GenerateDocumentButton
                            generateIcon={<FilePlus01 width={16} height={16} />}
                            label="Vygenerovat zakázkový list"
                            onClick={() => setOrderFormOpen(true)}
                          />
                        )}
                      </Stack>
                    </Stack>
                    {/*<TextField label="Poznámka" value={activeAccidentService?.note} disabled aria-readonly />*/}
                    <Box py={2}>
                      <Divider />
                    </Box>
                  </Stack>
                ) : (
                  <Button variant="outlined" onClick={() => setOpenServicesDialog(true)}>
                    Přiřadit servis
                  </Button>
                )}
                <Controller
                  control={control}
                  name="insuranceCode"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label="Číslo pojistné události"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.insuranceCode)}
                      helperText={errors.insuranceCode?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="date"
                  render={({ field: { onChange, value } }) => (
                    <DateTimePicker
                      label="Datum nehody"
                      value={value ? parseISO(value) : null}
                      onChange={(date) => onChange(date ? format(date, 'yyyy-MM-dd HH:mm') : '')}
                      format="dd.MM.yyyy HH:mm"
                      slotProps={{
                        textField: {
                          error: Boolean(errors.date),
                          helperText: errors.date?.message
                        }
                      }}
                    />
                  )}
                />
                {(accident?.source?.type === ACCIDENT_SOURCE_TYPE.TIP ||
                  accident?.source?.type === ACCIDENT_SOURCE_TYPE.SERVICE) && (
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Typography fontWeight="700">Zdroj nehody</Typography>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        value={[accident?.source?.name, accident?.source?.tradeName, accident?.source?.address]
                          .filter(Boolean)
                          .join(' - ')}
                        label={accident?.source?.type === ACCIDENT_SOURCE_TYPE.SERVICE ? 'Servis' : 'Tipař'}
                        color="secondary"
                        fullWidth
                        disabled
                        aria-readonly
                      />
                    </Grid>
                    <Grid size={9}>
                      <Controller
                        control={control}
                        name="source.commissionValue"
                        render={({ field: { onChange, value } }) => (
                          <TextField
                            value={value}
                            onChange={onChange}
                            label={
                              accident?.source?.type === ACCIDENT_SOURCE_TYPE.SERVICE
                                ? 'Provize servisu'
                                : 'Provize tipaři'
                            }
                            color="secondary"
                            fullWidth
                            error={Boolean(errors.source?.commissionValue)}
                            helperText={errors.source?.commissionValue?.message}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={3}>
                      <FormControl fullWidth>
                        <InputLabel id="commissionType-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                          Typ provize
                        </InputLabel>
                        <Select
                          value={commissionType}
                          onChange={(e) => setCommissionType(e.target.value)}
                          labelId="commissionType-label"
                        >
                          <MenuItem value="Kč">Kč</MenuItem>
                          <MenuItem value="%">%</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}
                <Controller
                  control={control}
                  name="car.wasDamagedBefore"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={Boolean(field.value)} onChange={(_, c) => field.onChange(c)} />}
                      label="Bylo vozidlo již poškozené"
                    />
                  )}
                />
                <FormControl fullWidth>
                  <FormLabel id="totalCrash-label">Totální škoda</FormLabel>
                  <Controller
                    control={control}
                    name="car.isTotalCrash"
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup aria-labelledby="totalCrash-label" value={value} onChange={onChange}>
                        <FormControlLabel value="0" control={<Radio />} label="Není totální škoda" />
                        <FormControlLabel value="1" control={<Radio />} label="Je totální škoda" />
                        <FormControlLabel value="2" control={<Radio />} label="Možná totální škoda" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
                <Controller
                  control={control}
                  name="car.hasLeasing"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={Boolean(field.value)} onChange={(_, c) => field.onChange(c)} />}
                      label="Je vozidlo na leasing"
                    />
                  )}
                />
                {hasLeasing && (
                  <Controller
                    control={control}
                    name="car.leasingNumber"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Číslo leasingové / úvěrové smlouvy"
                        color="secondary"
                        fullWidth
                        error={Boolean(errors.car?.leasingNumber)}
                        helperText={errors.car?.leasingNumber?.message}
                        InputLabelProps={{ shrink: true }}
                        onKeyDown={handleEnterAsTab}
                      />
                    )}
                  />
                )}
                <Controller
                  control={control}
                  name="car.location"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Místo, kde se automobil nachází"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.car?.location)}
                      helperText={errors.car?.location?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="car.contactPerson"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Kontaktní osoba v případě prohlídky"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.car?.contactPerson)}
                      helperText={errors.car?.contactPerson?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="isPoliceHandled"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={Boolean(field.value)} onChange={(_, c) => field.onChange(c)} />}
                      label="Řešeno policií"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="hasParking"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={Boolean(field.value)} onChange={(_, c) => field.onChange(c)} />}
                      label="Parkovné"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="hasTowing"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={Boolean(field.value)} onChange={(_, c) => field.onChange(c)} />}
                      label="Odtah"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="hasPhotos"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={Boolean(field.value)} onChange={(_, c) => field.onChange(c)} />}
                      label="Byla pořízena fotodokumentace"
                    />
                  )}
                />
                <FormControl fullWidth>
                  <InputLabel id="payoutType-label" sx={{ backgroundColor: 'white', px: 0.5 }}>
                    Kde poslat peníze
                  </InputLabel>
                  <Controller
                    control={control}
                    name="payoutType"
                    render={({ field }) => (
                      <Select {...field} labelId="payoutType-label">
                        <MenuItem value={PayoutType.BANK_ACCOUNT}>Bankovní účet</MenuItem>
                        <MenuItem value={PayoutType.ADDRESS}>Adresa trvalého bydliště</MenuItem>
                        <MenuItem value={PayoutType.INVOICE}>Na fakturu</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
                <Controller
                  control={control}
                  name="payoutInfo"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="č.ú. / adresa trvalého bydliště"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.payoutInfo)}
                      helperText={errors.payoutInfo?.message}
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
              </Stack>
            </BoxWithHeader>
          </Grid>

          {/* ══════════════════════════════════════════════════
              SEKCE 2: Účastníci
          ══════════════════════════════════════════════════ */}

          {/* Majitel vozu */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <BoxWithHeader
              title="Majitel vozu"
              fullHeight
              hasError={!accident.status?.progress?.groups?.user?.isCompleted}
              header={
                !accident.status?.progress?.groups?.user?.isCompleted && (
                  <BoxHeaderChip label="Vyžaduje doplnění" color="error" />
                )
              }
            >
              <Box mt={2}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Typ osoby</FormLabel>
                  <RadioGroup
                    row
                    value={userType || AccidentUserType.Person}
                    onChange={(e) => setUserType(e.target.value as AccidentUserType)}
                  >
                    <FormControlLabel value={AccidentUserType.Person} control={<Radio />} label="Fyzická osoba" />
                    <FormControlLabel value={AccidentUserType.Company} control={<Radio />} label="Firma" />
                    <FormControlLabel value={AccidentUserType.BusinessMan} control={<Radio />} label="Podnikatel" />
                  </RadioGroup>
                </FormControl>
                <ParticipantFields
                  prefix="user"
                  control={control}
                  userType={userType ?? AccidentUserType.Person}
                  ico={ico}
                  birthNumber={birthNumber}
                  isAresDataLoading={isAresDataLoading}
                  countryList={countryList}
                  autocompleteResults={autocompleteItems ?? []}
                  onAresClick={() => handleAresClick(ico, false)}
                  onSystemSearch={() => handleGetExistingUser(isCompany ? ico : birthNumber ?? '', false)}
                  onBirthNumberSearch={() => handleGetExistingUser(birthNumber ?? '', false)}
                  onAutocompleteSearch={(v) => handleAutocompleteSearch(v, false)}
                  onAutocompleteSelect={(v) => handleAutocompleteSelect(v, false)}
                  isLoadingAddressData={isLoadingData}
                />
              </Box>
            </BoxWithHeader>
          </Grid>

          {/* Provozovatel vozidla */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <BoxWithHeader title="Provozovatel vozidla" fullHeight>
              <FormControlLabel
                control={
                  <Switch
                    checked={operatorSameAsOwner}
                    onChange={(_, checked) => handleOperatorSameAsOwnerChange(checked)}
                  />
                }
                label="Stejný jako majitel"
              />
              {!operatorSameAsOwner && (
                <Box mt={2}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Typ osoby</FormLabel>
                    <RadioGroup
                      row
                      value={operatorType || AccidentUserType.Person}
                      onChange={(e) => setOperatorType(e.target.value as AccidentUserType)}
                    >
                      <FormControlLabel value={AccidentUserType.Person} control={<Radio />} label="Fyzická osoba" />
                      <FormControlLabel value={AccidentUserType.Company} control={<Radio />} label="Firma" />
                      <FormControlLabel value={AccidentUserType.BusinessMan} control={<Radio />} label="Podnikatel" />
                    </RadioGroup>
                  </FormControl>
                  <ParticipantFields
                    prefix="operator"
                    control={control}
                    userType={operatorType ?? AccidentUserType.Person}
                    ico={operatorIco}
                    birthNumber={operatorBirthNumber}
                    isAresDataLoading={isAresDataLoading}
                    countryList={countryList}
                    autocompleteResults={operatorAutocompleteItems ?? []}
                    onAresClick={() => handleAresClick(operatorIco, true)}
                    onSystemSearch={() =>
                      handleGetExistingUser(isOperatorCompany ? operatorIco : operatorBirthNumber ?? '', true)
                    }
                    onBirthNumberSearch={() => handleGetExistingUser(operatorBirthNumber ?? '', true)}
                    onAutocompleteSearch={(v) => handleAutocompleteSearch(v, true)}
                    onAutocompleteSelect={(v) => handleAutocompleteSelect(v, true)}
                    isLoadingAddressData={isLoadingOperatorData}
                  />
                </Box>
              )}
            </BoxWithHeader>
          </Grid>

          {/* Řidič vozu */}
          <Grid size={{ xs: 12, sm: 6, xl: 4 }}>
            <BoxWithHeader
              title="Řidič vozu"
              fullHeight
              hasError={!accident.status?.progress?.groups?.driver?.isCompleted}
              header={
                !accident.status?.progress?.groups?.driver?.isCompleted && (
                  <BoxHeaderChip label="Vyžaduje doplnění" color="error" />
                )
              }
            >
              <Box mt={2}>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={driverSameAsOwner || 'none'}
                    onChange={(e) => setDriverSameAsOwner(e.target.value as SameAsValue)}
                  >
                    {!shouldHideUserOptionForDriver && (
                      <FormControlLabel value="user" control={<Radio />} label="Stejný jako majitel" />
                    )}
                    {!shouldHideOperatorOptionForDriver && (
                      <FormControlLabel value="operator" control={<Radio />} label="Stejný jako provozovatel" />
                    )}
                    <FormControlLabel value="none" control={<Radio />} label="Jiný" />
                  </RadioGroup>
                </FormControl>
                {driverSameAsOwner === 'none' && (
                  <Stack gap={2} mt={2}>
                    <Grid container spacing={2}>
                      <Grid size={3}>
                        <Controller
                          control={control}
                          name="driver.degree"
                          render={({ field }) => (
                            <TextField
                              {...field}
                              value={field.value ?? ''}
                              label="Titul"
                              color="secondary"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              onKeyDown={handleEnterAsTab}
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={9}>
                        <Controller
                          control={control}
                          name="driver.name"
                          render={({ field }) => (
                            <TextField
                              {...field}
                              value={field.value ?? ''}
                              label="Jméno"
                              color="secondary"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              onKeyDown={handleEnterAsTab}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Controller
                      control={control}
                      name="driver.surname"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          label="Příjmení"
                          color="secondary"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          onKeyDown={handleEnterAsTab}
                        />
                      )}
                    />
                    <BirthNumberFields
                      prefix="driver"
                      control={control}
                      birthNumber={driverBirthNumber}
                      onSearch={() => {}}
                      showSearchIcon={false}
                    />
                  </Stack>
                )}
              </Box>
            </BoxWithHeader>
          </Grid>

          {/* Kontaktní osoba */}
          <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
            <BoxWithHeader
              title="Kontaktní osoba"
              fullHeight
              hasError={!accident.status?.progress?.groups?.contactPerson?.isCompleted}
              header={
                !accident.status?.progress?.groups?.contactPerson?.isCompleted && (
                  <BoxHeaderChip label="Vyžaduje doplnění" color="error" />
                )
              }
            >
              <Stack gap={2}>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={contactSameAsOwner || 'none'}
                    onChange={(e) => setContactSameAsOwner(e.target.value as SameAsValue)}
                  >
                    {!shouldHideUserOptionForContact && (
                      <FormControlLabel value="user" control={<Radio />} label="Stejný jako majitel" />
                    )}
                    {!shouldHideOperatorOptionForContact && (
                      <FormControlLabel value="operator" control={<Radio />} label="Stejný jako provozovatel" />
                    )}
                    {!shouldHideDriverOptionForContact && (
                      <FormControlLabel value="driver" control={<Radio />} label="Stejný jako řidič" />
                    )}
                    <FormControlLabel value="none" control={<Radio />} label="Jiný" />
                  </RadioGroup>
                </FormControl>
                {contactSameAsOwner === 'none' && (
                  <>
                    <Grid container spacing={2}>
                      <Grid size={3}>
                        <Controller
                          control={control}
                          name="contactPerson.degree"
                          render={({ field: { onChange, value } }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              label="Titul"
                              color="secondary"
                              fullWidth
                              error={Boolean(errors.contactPerson?.degree)}
                              helperText={errors.contactPerson?.degree?.message}
                              InputLabelProps={{ shrink: true }}
                              onKeyDown={handleEnterAsTab}
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={9}>
                        <Controller
                          control={control}
                          name="contactPerson.name"
                          render={({ field: { onChange, value } }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              label="Jméno"
                              color="secondary"
                              fullWidth
                              error={Boolean(errors.contactPerson?.name)}
                              helperText={errors.contactPerson?.name?.message}
                              InputLabelProps={{ shrink: true }}
                              onKeyDown={handleEnterAsTab}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Controller
                      control={control}
                      name="contactPerson.surname"
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          value={value}
                          onChange={onChange}
                          label="Přijmení"
                          color="secondary"
                          fullWidth
                          error={Boolean(errors.contactPerson?.surname)}
                          helperText={errors.contactPerson?.surname?.message}
                          InputLabelProps={{ shrink: true }}
                          onKeyDown={handleEnterAsTab}
                        />
                      )}
                    />
                    <BirthNumberFields
                      prefix="contactPerson"
                      control={control}
                      birthNumber={watch('contactPerson.birthNumber')}
                      onSearch={() => {}}
                      showSearchIcon={false}
                      showBirthDate={false}
                    />
                    <Controller
                      control={control}
                      name="contactPerson.phone"
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          value={value ?? ''}
                          onChange={onChange}
                          label="Telefon"
                          color="secondary"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          onKeyDown={handleEnterAsTab}
                        />
                      )}
                    />
                  </>
                )}
              </Stack>
            </BoxWithHeader>
          </Grid>

          {/* Notifikace */}
          <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
            <BoxWithHeader title="Notifikace" fullHeight>
              <Stack gap={2}>
                <Grid container spacing={2}>
                  <Grid size={9}>
                    <Controller
                      control={control}
                      name="notification.email"
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          value={value ?? ''}
                          onChange={onChange}
                          type="email"
                          label="E-mail"
                          color="secondary"
                          fullWidth
                          helperText="Slouží pro zaslání informačního emailu"
                          InputLabelProps={{ shrink: true }}
                          onKeyDown={handleEnterAsTab}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={3}>
                    <Tooltip title="Vyhledat v systému">
                      <Button
                        variant="contained"
                        color="info"
                        onClick={() => handleGetAccountEmail(notificationEmail)}
                        disabled={!notificationEmail}
                        sx={{ height: '56px' }}
                      >
                        <SearchLG />
                      </Button>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Controller
                  control={control}
                  name="notification.phone"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value ?? ''}
                      onChange={onChange}
                      label="Telefon"
                      color="secondary"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      onKeyDown={handleEnterAsTab}
                    />
                  )}
                />
                {accountResult && typeof accountResult === 'object' && accountResult.email && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {` Tento e-mail: ${accountResult.email} je přiřazen existujícímu účtu. Nehoda bude
                      zobrazena v aplikaci.`}
                  </Alert>
                )}
                {accountResult === false && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3
                    }}
                  >
                    <Stack spacing={1}>
                      <Box>Tento e-mail není přiřazen účtu, chcete účet vytvořit?</Box>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => setAddNewAccountDialogOpen(true)}
                      >
                        Vytvořit účet
                      </Button>
                    </Stack>
                  </Alert>
                )}
              </Stack>
            </BoxWithHeader>
          </Grid>

          {/* ══════════════════════════════════════════════════
              SEKCE 3: Dokumenty, korespondence, faktury
          ══════════════════════════════════════════════════ */}

          <Grid size={{ xs: 12, lg: 12, xl: 8 }}>
            <BoxWithHeader
              title="Dokumenty k nehodě"
              fullHeight
              hasError={!accident.status?.progress?.groups?.mediaFiles?.isCompleted}
              header={
                !accident.status?.progress?.groups?.mediaFiles?.isCompleted && (
                  <BoxHeaderChip label="Vyžaduje doplnění" color="error" />
                )
              }
            >
              <Stack gap={2}>
                {[
                  {
                    title: 'Plná moc',
                    template: ACCIDENT_FILES.POWER_OF_ATTORNEY,
                    chooseTenant: true
                  },
                  { title: 'GDPR', template: ACCIDENT_FILES.GDPR },
                  {
                    title: 'Doplňující informace k nehodě',
                    template: ACCIDENT_FILES.ADDITIONAL_INFO
                  },
                  { title: 'Lísteček', template: ACCIDENT_FILES.LEAFLET }
                ].map(({ title, template, chooseTenant }) => {
                  const document = accident?.documents?.find((doc) => doc.template === template);
                  if (!document) return null;
                  return (
                    <Stack key={template}>
                      <LineClamp maxLines={1} fontWeight="bold">
                        {title}
                      </LineClamp>
                      <Box my={1}>
                        <Divider />
                      </Box>
                      <Stack
                        alignItems={{ md: 'center' }}
                        direction={{ md: 'row' }}
                        justifyContent="space-between"
                        width={1}
                      >
                        <BasicDocumentValueItem
                          fileName={title}
                          url={document.path}
                          createdDate={document?.updatedDate}
                          component={document.path ? 'a' : 'p'}
                        />
                        <Stack direction="row" mt={{ xs: 1, md: 0 }}>
                          <DocumentActions
                            document={document}
                            template={document.template as ACCIDENT_FILES}
                            isGeneratingDocument={isGeneratingDocument}
                            handleGenerateDocument={handleGenerateDocument}
                            chooseTenant={chooseTenant}
                            setSelectedDocumentName={(t, tenant) =>
                              setSelectedDocumentName({
                                documentName: t,
                                tenant
                              })
                            }
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
              <Stack gap={2} mt={2}>
                {accident.media?.files
                  ?.filter((f) => f.values && !isEmpty(f.values))
                  .map((file, index) => (
                    <DocumentItem
                      key={index}
                      title={file.label}
                      {...file}
                      onRemove={(id) => handleRemoveFile(file.name, id)}
                      onClick={() => onModalOpen(file.name, file.limit, file.label)}
                    />
                  ))}
              </Stack>
              <Stack gap={1} mt={2}>
                {accident.media?.files
                  ?.filter((f) => !f.values || isEmpty(f.values))
                  .map((file, index) => (
                    <DocumentItemToUpload
                      key={index}
                      title={file.label}
                      limit={file.limit}
                      onClick={() => onModalOpen(file.name, file.limit, file.label)}
                      uploadNecessary={
                        file.required && accident?.status?.progress?.groups?.mediaFiles?.missing?.includes(file.name)
                      }
                    />
                  ))}
              </Stack>
            </BoxWithHeader>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <BoxWithHeader title="Korespondence" fullHeight>
              <Stack gap={2}>
                {accident.media?.communicationFiles?.map((file, index) => (
                  <DocumentItem
                    key={index}
                    title={file.label}
                    {...file}
                    onRemove={(id) => handleRemoveFile(file.name, id)}
                    onClick={() => onModalOpen(file.name, file.limit, file.label)}
                  />
                ))}
              </Stack>
            </BoxWithHeader>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <BoxWithHeader title="Servisní dokumenty" fullHeight>
              <Stack gap={2}>
                {accident.media?.serviceFiles?.map((file, index) => (
                  <DocumentItem
                    key={index}
                    title={file.label}
                    {...file}
                    onRemove={(id) => handleRemoveFile(file.name, id)}
                    onClick={() => onModalOpen(file.name, file.limit, file.label)}
                  />
                ))}
              </Stack>
            </BoxWithHeader>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <BoxWithHeader title="Vystavené faktury" fullHeight>
              <Stack gap={2}>
                {accident.accountingDocuments?.map((document) => (
                  <BasicDocumentValueItem
                    key={document.number}
                    fileName={document.number}
                    url={document.path}
                    component="a"
                  />
                ))}
              </Stack>
            </BoxWithHeader>
          </Grid>

          {/* ══════════════════════════════════════════════════
              SEKCE 4: Poznámky
          ══════════════════════════════════════════════════ */}

          <Grid size={{ xs: 12 }}>
            <BoxWithHeader title="Poznámky" fullHeight>
              <Stack spacing={2}>
                <Controller
                  control={control}
                  name="note"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label="Poznámka k nehodě"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.note)}
                      helperText={errors.note?.message}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={5}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="carDamageNote"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label="Poškození vozidla"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.carDamageNote)}
                      helperText={errors.carDamageNote?.message}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={5}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="internalNote"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      label="Interní poznámka"
                      color="secondary"
                      fullWidth
                      error={Boolean(errors.internalNote)}
                      helperText={errors.internalNote?.message}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={5}
                    />
                  )}
                />
              </Stack>
            </BoxWithHeader>
          </Grid>

          {/* ══════════════════════════════════════════════════
              SEKCE 5: Fotografie
          ══════════════════════════════════════════════════ */}

          <Grid size={12}>
            <Typography variant="h6" fontWeight="bold">
              Fotografie z nehody
            </Typography>
          </Grid>
          {user?.role === Role.Dealer ? (
            <Grid size={12}>
              <Grid container spacing={2}>
                {accident?.media?.images?.map((image, index) => (
                  <Grid size={{ xs: 12, md: 6 }} key={index}>
                    <AccidentPhotographyBox
                      title={image.label}
                      onUpload={(photo) => handleSetImage?.(image.name, photo, true)}
                      defaultPhotos={image?.values as MediaValue[]}
                      onRemove={(fileId) => handleRemoveFile?.(image.name, fileId)}
                      showThumbs={false}
                      note={image.note}
                      onNoteSave={(value) =>
                        debouncedUpdatePhotoNote({
                          note: value,
                          photoName: parseAccidentPhotoNoteToAccident(image.name)
                        })
                      }
                    />
                  </Grid>
                ))}
                <Backdrop
                  sx={(theme) => ({
                    color: '#fff',
                    zIndex: theme.zIndex.drawer + 1
                  })}
                  open={isUploadingImages || isRemoving}
                >
                  <CircularProgress color="inherit" />
                </Backdrop>
              </Grid>
            </Grid>
          ) : (
            <Grid size={12}>
              <BoxWithHeader title="Fotky vozu z nehody">
                <Grid container mt={3} spacing={3}>
                  {accident?.media?.images && <AccidentsPhotos photos={accident?.media?.images} />}
                </Grid>
              </BoxWithHeader>
            </Grid>
          )}
        </Grid>
      </form>

      {/* ── Modaly ── */}

      <RentalCarSelectionModal
        open={!!availableCars && !isEmpty(availableCars)}
        close={() => setAvailableCars(undefined)}
        availableCars={availableCars}
        onCarSearch={handleGetAvailableRentalCars}
        isSearching={isSearchingRentalCars}
        onCarRent={(rentData, callbacks) => {
          handleRentCar(rentData, {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ['common', 'accidents', 'detail', accident.id.toString()]
              });
              callbacks?.onSuccess?.();
            }
          });
        }}
        userId={Number.parseInt(user?.id ?? '')}
        accidentId={accident.id}
        isProcessingRent={isProcessingRent}
      />

      <SignatureDialog
        open={!!selectedDocumentName}
        onClose={() => setSelectedDocumentName(null)}
        onSave={async (signatureBase64) => handleSignDocument(signatureBase64)}
        loading={isGeneratingDocument}
      />

      <BasicDialog
        open={changeSourceModalOpen}
        onClose={() => setChangeSourceModalOpen(false)}
        title="Upravit zdroj nehody"
        maxWidth="lg"
      >
        <DialogContent sx={{ p: 0, minWidth: { md: 600 }, maxWidth: '100%' }}>
          <AcceptanceContent
            reasonValue={reasonValue}
            onReasonChange={setReasonValue}
            insuranceValue={insuranceValue}
            onSourceDetailChange={setReasonValue}
            onPlaceChange={setOnPlace}
            onPlaceValue={onPlace}
            acceptanceReasons={acceptanceSources}
            accidentDate={accidentDate}
            onDateChange={setAccidentDate}
            isEdit
          />
          <Box py={2}>
            <Divider />
          </Box>
          <Stack mt={1}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSubmitReasonChange}
              startIcon={<Save01 size="20px" />}
            >
              Upravit zdroj
            </Button>
          </Stack>
        </DialogContent>
      </BasicDialog>

      <ServiceOrderFormDialog
        open={orderFormOpen}
        onClose={() => setOrderFormOpen(false)}
        accidentId={accident.id}
        hasOrderForm={Boolean(activeAccidentService?.orderForm)}
      />

      <AccidentAssigneeServiceDialog
        services={servicesTypes}
        open={openServicesDialog}
        onClose={() => setOpenServicesDialog(false)}
        isLoadingServices={isLoadingServices}
        assigneeServiceToAccident={handleAssigneeServiceToAccident}
        isSaving={isAssigningServiceToAccident}
        defaultData={activeAccidentService}
        defaultDamageNote={accident?.carDamageNote}
      />

      <AddNewAccountDialog
        open={addNewAccountDialogOpen}
        onClose={() => setAddNewAccountDialogOpen(false)}
        onSubmit={handleCreateAccount}
        isLoading={isCreatingAccount}
        email={notificationEmail}
        phone={notificationPhone}
        userData={
          userType !== AccidentUserType.Company
            ? {
                degree: watch('user.degree'),
                name: watch('user.name'),
                surname: watch('user.surname')
              }
            : undefined
        }
        operatorData={
          !operatorSameAsOwner && operatorType !== AccidentUserType.Company
            ? {
                degree: watch('operator.degree'),
                name: watch('operator.name'),
                surname: watch('operator.surname')
              }
            : undefined
        }
      />
    </>
  );
};
